import { ChevronsUpDown, Plus, X } from "lucide-react";
import { Button } from "../button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "../dropdown-menu";
import { HGFocus, HGStacks } from "@/stack/stacks";
import { HGInterval, HGIntervalType } from "@/common/types";
import {
  getChildrenTypes,
  getChildType,
  getPlural,
} from "@/common/intervalTypes";
import { safeName, safeShortName } from "@/common/recordTree";
import { capitalize } from "@/common/utils";
import { MapIntervals } from "@/engine/engine";
import {
  IntervalQualifiers as Qualifiers,
  qualifierLabel,
  qualifyName,
} from "@/common/qualifiers";
import { QualifierToggles } from "./IntervalQualifiers";

// Hidden for now — not sure why it's useful, but keep it around in case it's
// needed again.
const SHOW_BREADCRUMBS = false;

/**
 * The mode indicator for the interval list below it: which endpoint the list
 * is editing (From / To), where that endpoint sits in the hierarchy, and at
 * which level. Without this the list is ambiguous, so it lives inside
 * StacksWidget rather than in each host — the sidebar and the record dialog
 * cannot drift apart.
 */
export interface StacksQualifiers {
  start: Qualifiers;
  end: Qualifiers;
  /** Reports which endpoint was edited, since the toggles follow the focus. */
  onChange(focus: HGFocus, value: Qualifiers): void;
}

export default function StacksHeader({
  stacks,
  singleMode,
  qualifiers,
}: {
  stacks: HGStacks;
  /** No end endpoint is allowed (an ongoing record has only a start). */
  singleMode?: boolean;
  /**
   * EDTF qualifiers for the two endpoints. Supplied only where the picker edits
   * a record; without them the header is the plain navigator it has always been.
   */
  qualifiers?: StacksQualifiers;
}) {
  const focus = stacks.getFocus();
  const startInterval = stacks.getInterval();
  const endInterval = stacks.getEndInterval();
  const focusedStack = stacks.getFocusedStack();
  const focusedInterval = focusedStack.getInterval();
  const activeIt = focusedInterval.it;

  // A range's end keeps its *last* second fixed when it changes level, so its
  // ancestors are read off the end of the span rather than the beginning.
  // Matters only where a level straddles its parent (a week spanning two
  // months), but getting it wrong there would name the wrong ancestor.
  const anchorEnd = focus === "end";
  const crumbs: HGInterval[] = focusedStack.getParentIts().map((it) => {
    const span = MapIntervals(focusedInterval, null, it);
    return { n: anchorEnd ? span.end.n : span.begin.n, it };
  });

  // getChildType() can name a type outside childrenTypes (a century's default
  // child is "y", not "dec"), so a stack can sit at a level the sibling list
  // does not contain — which would leave the radio group with nothing checked.
  const parentIt = focusedStack.getParentIt();
  const siblingIts = getChildrenTypes(parentIt);
  const preferredIt = getChildType(parentIt);
  const levelIts = [
    ...(preferredIt && !siblingIts.includes(preferredIt) ? [preferredIt] : []),
    ...siblingIts,
  ];
  const levelOptions = levelIts.includes(activeIt)
    ? levelIts
    : [activeIt, ...levelIts];

  const chip = (label: string, target: HGFocus, interval: HGInterval) => {
    const active = focus === target;
    const q = target === "end" ? qualifiers?.end : qualifiers?.start;
    // The qualified name is the whole feedback loop for the toggles below: they
    // edit the focused endpoint, and this is where that edit shows up.
    const name = q ? qualifyName(safeName(interval), q) : safeName(interval);
    const hint = q && qualifierLabel(q);
    return (
      <button
        type="button"
        aria-pressed={active}
        title={hint ? `${name} — ${hint}` : name}
        onClick={() => stacks.setFocus(target)}
        className={
          "flex min-h-11 min-w-0 flex-1 touch-manipulation flex-col items-start justify-center rounded-md border px-2.5 py-1 text-left transition-colors " +
          (active
            ? "border-primary/60 bg-primary/10"
            : "border-border bg-card hover:bg-row-hover")
        }
      >
        <span
          className={
            "text-[10px] font-medium uppercase tracking-wide " +
            (active ? "text-primary" : "text-ink2")
          }
        >
          {label}
        </span>
        <span className="w-full truncate text-[13px] leading-tight">
          {name}
        </span>
      </button>
    );
  };

  return (
    <div className="flex shrink-0 flex-col gap-1.5 border-b px-3 pt-2 pb-1.5">
      <div className="flex items-stretch gap-1">
        {chip(
          endInterval ? "From" : singleMode ? "Since" : "When",
          "start",
          startInterval,
        )}
        {endInterval ? (
          <>
            {chip("To", "end", endInterval)}
            {!singleMode && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                title="Remove the end and select a single interval"
                aria-label="Remove end"
                onClick={() => stacks.removeEndStack()}
                className="h-auto min-h-11 w-8 shrink-0 text-ink2 hover:bg-row-hover"
              >
                <X className="size-3.5" />
              </Button>
            )}
          </>
        ) : (
          !singleMode && (
            <button
              type="button"
              title="Extend the selection into a range"
              onClick={() => stacks.addEndStack()}
              className="flex min-h-11 flex-1 touch-manipulation items-center justify-center gap-1 rounded-md border border-dashed px-2.5 text-[12.5px] text-ink2 transition-colors hover:bg-row-hover hover:text-foreground"
            >
              <Plus className="size-3.5" />
              Add end
            </button>
          )
        )}
      </div>

      <div className="flex min-w-0 items-center gap-1">
        {qualifiers ? (
          // The toggles ride in the row's free space and follow the focus, so
          // qualifying a date costs no height of its own. getFocus() reports
          // "start" whenever there is no end stack, so a single-date record can
          // only ever qualify its start.
          <QualifierToggles
            className="grow"
            value={focus === "end" ? qualifiers.end : qualifiers.start}
            onChange={(value) => qualifiers.onChange(focus, value)}
          />
        ) : (
          <div className="flex min-w-0 grow items-center gap-0.5 overflow-x-auto [&::-webkit-scrollbar]:hidden">
            {SHOW_BREADCRUMBS &&
              crumbs.map((crumb, i) => (
                <span key={crumb.it} className="flex shrink-0 items-center">
                  {i > 0 && <span className="px-0.5 text-ink2/60">›</span>}
                  <button
                    type="button"
                    title={safeName(crumb)}
                    // The immediate parent goes through popFocused() so the
                    // level change retraces push()'s own chain; anything higher
                    // is a multi-pop, which setInterval() handles by truncating.
                    onClick={() =>
                      i === crumbs.length - 1
                        ? stacks.popFocused()
                        : stacks.setFocusedInterval(crumb)
                    }
                    className="rounded px-1 py-0.5 text-[12px] whitespace-nowrap text-ink2 transition-colors hover:bg-row-hover hover:text-foreground"
                  >
                    {safeShortName(crumb)}
                  </button>
                </span>
              ))}
          </div>
        )}

        {levelOptions.length > 1 ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 shrink-0 justify-between gap-1 px-2 text-[12.5px] font-normal text-ink2 hover:bg-row-hover data-[state=open]:bg-row-hover"
              >
                <span className="truncate">
                  {capitalize(getPlural(activeIt))}
                </span>
                <ChevronsUpDown className="size-3.5 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-40">
              <DropdownMenuRadioGroup
                value={activeIt}
                onValueChange={(it) =>
                  stacks.setFocusedIt(it as HGIntervalType)
                }
              >
                {levelOptions.map((it) => (
                  <DropdownMenuRadioItem
                    key={it}
                    value={it}
                    className="text-[13px]"
                  >
                    {capitalize(getPlural(it))}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <div className="flex h-7 shrink-0 items-center px-2 text-[12.5px] text-ink2">
            {capitalize(getPlural(activeIt))}
          </div>
        )}
      </div>
    </div>
  );
}
